import { apiClient } from './_shared.js'

/**
 * Вспомогательная функция для вызова серверного API /api/ivideon
 */
async function callIvideonApi(action, extraBody = {}) {
  return await apiClient('/ivideon', {
    method: 'POST',
    body: JSON.stringify({ action, ...extraBody }),
  })
}

/**
 * Проверить, доступен ли OAuth2 (настроен ли client_id на сервере).
 */
export async function checkIvideonOAuth() {
  return callIvideonApi('check_oauth')
}

/**
 * Начать OAuth2 авторизацию — получить URL для редиректа на Ivideon.
 */
export async function startIvideonOAuth() {
  return callIvideonApi('oauth_start')
}

/**
 * Завершить OAuth2 — обменять code на access_token.
 */
export async function completeIvideonOAuth(code, state) {
  return callIvideonApi('oauth_callback', { code, state })
}

/**
 * Проверить API-ключ Ivideon (без сохранения).
 * Ключ формата xtok-... создаётся в ЛК Ivideon → Ключи доступа к API.
 */
export async function testIvideonKey(apiKey) {
  return callIvideonApi('test_key', { api_key: apiKey })
}

/**
 * Сохранить API-ключ Ivideon.
 */
export async function saveIvideonKey(apiKey) {
  return callIvideonApi('save_key', { api_key: apiKey })
}

/**
 * Получить текущую конфигурацию Ivideon.
 */
export async function getIvideonConfig() {
  return callIvideonApi('get_config')
}

/**
 * Получить список камер из Ivideon Open API.
 */
export async function listIvideonCameras() {
  return callIvideonApi('list_cameras')
}

/**
 * Отключить интеграцию Ivideon (удалить ключ).
 */
export async function disconnectIvideon() {
  return callIvideonApi('disconnect')
}

/**
 * Синхронизировать выбранные камеры Ivideon в таблицу cameras.
 */
export async function syncIvideonCameras(orgId, pvzId, selectedCameras) {
  return await apiClient('/cameras/ivideon/sync', {
    method: 'POST',
    body: JSON.stringify({ orgId, pvzId, selectedCameras })
  })
}

/**
 * Получить HLS stream URLs для всех Ivideon-камер.
 * Возвращает { urls: { camera_id: { hls, fullId } }, apiKey }
 */
export async function getIvideonStreamUrls() {
  return callIvideonApi('get_stream_urls')
}

/**
 * Получить HLS URL живого стрима для одной камеры.
 * @param {string} cameraFullId - полный ID камеры (server:cam)
 */
export async function getIvideonLiveUrl(cameraFullId) {
  return callIvideonApi('get_live_url', { camera_full_id: cameraFullId })
}

/**
 * Получить URL снапшота камеры.
 * @param {string} cameraFullId - полный ID камеры
 */
export async function getIvideonSnapshot(cameraFullId) {
  return callIvideonApi('get_snapshot', { camera_full_id: cameraFullId })
}

/**
 * Получить HLS URL архивного стрима.
 * @param {string} cameraFullId - полный ID камеры
 * @param {number} start - Unix timestamp начала
 * @param {number} end - Unix timestamp конца (опционально)
 */
export async function getIvideonArchiveUrl(cameraFullId, start, end) {
  return callIvideonApi('get_archive_url', { camera_full_id: cameraFullId, start, end })
}

/**
 * Получить таймлайн архива (список сегментов записей).
 */
export async function getIvideonArchiveTimeline(cameraFullId, start, end) {
  return callIvideonApi('get_archive_timeline', { camera_full_id: cameraFullId, start, end })
}

/**
 * Получить URL снапшота архива (JPEG кадр из записи).
 */
export async function getIvideonArchiveSnapshot(cameraFullId, time) {
  return callIvideonApi('get_archive_snapshot', { camera_full_id: cameraFullId, time })
}

/**
 * Получить календарь архива (какие дни имеют записи).
 */
export async function getIvideonArchiveCalendar(cameraFullId, month) {
  return callIvideonApi('get_archive_calendar', { camera_full_id: cameraFullId, month })
}

/**
 * Создать запрос на экспорт MP4 видео.
 * @param {string} cameraFullId - полный ID камеры
 * @param {number} start - Unix timestamp начала
 * @param {number} end - Unix timestamp конца
 */
export async function exportIvideonMp4(cameraFullId, start, end) {
  return callIvideonApi('export_mp4', { camera_full_id: cameraFullId, start, end })
}

/**
 * Проверить статус экспорта.
 * @param {string} exportId - ID экспорта
 */
export async function checkIvideonExport(exportId) {
  return callIvideonApi('check_export', { export_id: exportId })
}

/**
 * Удалить экспортированную запись (очистка).
 */
export async function deleteIvideonExport(exportId) {
  return callIvideonApi('delete_export', { export_id: exportId })
}
